import { useState, useRef } from "react";
import { DEFAULT_ID_FIELD } from 'constants/common/data-table-constants';

/**
 * Custom hook for managing table scroll functionality
 * @param {Object} options - Configuration options
 * @param {string} options.idField - Field name to use for row identification (default: 'id')
 * @param {string} options.highlightColumn - Column accessor to highlight (if not provided, highlights entire row)
 * @param {Function} options.onScrollError - Callback when scroll fails
 * @param {Function} options.onScrollSuccess - Callback when scroll succeeds
 */
export const useTableScroll = (options = {}) => {
  const {
    idField = DEFAULT_ID_FIELD,
    highlightColumn,
    onScrollError,
    onScrollSuccess
  } = options;

  const [scrollToRowId, setScrollToRowId] = useState(null);
  const [highlightedCellInfo, setHighlightedCellInfo] = useState(null);
  const tableRef = useRef(null);

  const handleScrollToRowByDataId = (dataId) => {
    if (!tableRef.current) {
      const errorMsg = 'Table not ready to scroll';
      onScrollError?.(errorMsg);
      return;
    }
    
    const allRows = tableRef.current.getRowModel().flatRows;
    const matchingRow = allRows.find(row => row.original[idField] === dataId);
    
    if (matchingRow) {
      setScrollToRowId({ dataId, timestamp: Date.now() });
      setHighlightedCellInfo({
        rowId: matchingRow.id,
        columnAccessor: highlightColumn
      });
      onScrollSuccess?.(matchingRow);
    } else {
      const errorMsg = `No row found with ${idField}: ${dataId}`;
      onScrollError?.(errorMsg, dataId);
    }
  };

  return {
    scrollToRowId,
    highlightedCellInfo,
    tableRef,
    handleScrollToRowByDataId
  };
};
